import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, X, Film, AlertTriangle } from "lucide-react";

import { confirmar } from "@/components/ConfirmDialog";
const fmtSize = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : `${Math.max(1, Math.round(b / 1024 ** 2))} MB`);

/** Lê a duração e confere se o navegador consegue tocar o arquivo (antes de enviar). */
function probe(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (r) => { URL.revokeObjectURL(url); v.removeAttribute("src"); resolve(r); };
    const t = setTimeout(() => done({ ok: false, duration: 0 }), 15000);
    v.preload = "metadata";
    v.onloadedmetadata = () => { clearTimeout(t); done({ ok: v.videoWidth > 0 || v.duration > 0, duration: Math.round(v.duration || 0) }); };
    v.onerror = () => { clearTimeout(t); done({ ok: false, duration: 0 }); };
    v.src = url;
  });
}

/**
 * Envio de vídeo para a plataforma, em partes de 8 MB (arquivos grandes, retoma se uma parte falhar).
 * onDone({ id, name, size, duration })
 */
const DOC_EXT = ["pdf", "xlsx", "xls", "csv", "docx", "doc", "pptx", "ppt", "jpg", "jpeg", "png", "webp", "gif"];

export function VideoUpload({ onDone, folderId = "", docs = false }) {
  const input = useRef(null);
  const cancel = useRef(false);
  const [st, setSt] = useState(null);
  const [over, setOver] = useState(false); // { name, size, sent, phase: "check"|"send"|"finish" }

  // fila: { i, n } quando vários arquivos foram escolhidos de uma vez (envia um por vez)
  const send = async (file, fila = null) => {
    const erro = (m) => { toast.error(fila ? `${file.name}: ${m}` : m); return false; };
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const isDoc = docs && DOC_EXT.includes(ext);
    if (docs && ["heic", "heif"].includes(ext)) return erro("Foto de iPhone (HEIC) não abre em todos os aparelhos. Converta para JPG ou mande como \"Mais compatível\" e envie de novo.");
    if (!isDoc && !["mp4", "m4v", "mov", "webm"].includes(ext)) return erro(docs ? "Envie vídeo (MP4, MOV, WEBM), imagem (JPG, PNG, WEBP, GIF) ou arquivo PDF, Excel, Word, PowerPoint ou CSV." : "Envie um vídeo MP4 (recomendado), MOV ou WEBM.");
    if (isDoc && file.size > 200 * 1024 ** 2) return erro("O arquivo deve ter até 200 MB.");
    if (file.size > 6 * 1024 ** 3) return erro("O vídeo deve ter até 6 GB.");
    if (!fila) cancel.current = false;
    setSt({ name: file.name, size: file.size, sent: 0, phase: "check", fila });
    const info = isDoc ? { ok: true, duration: 0 } : await probe(file);
    if (!info.ok && !(await confirmar(
      "Este navegador não conseguiu abrir o vídeo. Pode ser um formato que não toca em todos os aparelhos " +
      "(ex.: vídeo de iPhone em HEVC). O ideal é MP4 (H.264).\n\nEnviar mesmo assim?"))) { if (!fila) setSt(null); return false; }
    try {
      const init = await api.post("/admin/videos/upload", { filename: file.name, size: file.size, folder_id: folderId || "" });
      const { upload_id: id, chunk_size: chunk } = init.data;
      let offset = 0;
      setSt((s) => ({ ...s, phase: "send" }));
      while (offset < file.size) {
        if (cancel.current) throw new Error("cancelado");
        const part = file.slice(offset, offset + chunk);
        let tries = 0;
        for (;;) {
          try {
            const r = await api.put(`/admin/videos/upload/${id}`, part, { params: { offset }, headers: { "Content-Type": "application/octet-stream" }, timeout: 300000 });
            offset = r.data.received;
            break;
          } catch (e) {
            if (e.response?.status === 409 && typeof e.response.data?.received === "number") { offset = e.response.data.received; break; }
            if (++tries >= 4 || (e.response && e.response.status < 500 && e.response.status !== 429)) throw e;
            await new Promise((ok) => setTimeout(ok, 2000 * tries));
          }
        }
        setSt((s) => ({ ...s, sent: offset }));
      }
      setSt((s) => ({ ...s, phase: "finish" }));
      const r = await api.post(`/admin/videos/upload/${id}/complete`, { duration: info.duration });
      onDone({ ...r.data, duration: info.duration });
      if (!fila) toast.success(isDoc ? (["jpg", "jpeg", "png", "webp", "gif"].includes(ext) ? "Imagem enviada!" : "Arquivo enviado!") : "Vídeo enviado!");
      return true;
    } catch (e) {
      if (e.message !== "cancelado") erro(formatApiError(e.response?.data?.detail) || "Falha ao enviar. Tente de novo.");
      return false;
    } finally {
      if (!fila) { setSt(null); if (input.current) input.current.value = ""; }
    }
  };

  const sendAll = async (lista) => {
    const files = Array.from(lista || []);
    if (files.length <= 1) { if (files[0]) await send(files[0]); return; }
    cancel.current = false;
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      if (cancel.current) break;
      if (await send(files[i], { i: i + 1, n: files.length })) ok++;
    }
    setSt(null);
    if (input.current) input.current.value = "";
    const falhas = files.length - ok;
    if (ok) toast.success(`${ok} de ${files.length} enviado(s)!${falhas ? ` ${falhas} não foi(ram) enviado(s): veja os avisos.` : ""}`);
    else if (!cancel.current) toast.error("Nenhum arquivo foi enviado.");
  };

  if (st) {
    const pct = st.size ? Math.floor((st.sent / st.size) * 100) : 0;
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 max-w-2xl" data-testid="video-upload-progress">
        <div className="flex items-center gap-3">
          <Film className="h-5 w-5 text-[#232D4F] shrink-0" />
          <div className="min-w-0 flex-1">
            {st.fila && <div className="text-xs font-semibold text-[#1E9E2A]">Arquivo {st.fila.i} de {st.fila.n}</div>}
            <div className="text-sm font-medium text-slate-800 truncate">{st.name}</div>
            <div className="text-xs text-slate-500">
              {st.phase === "check" ? "Conferindo o arquivo…" : st.phase === "finish" ? "Finalizando…" : `${fmtSize(st.sent)} de ${fmtSize(st.size)} · ${pct}%`}
            </div>
          </div>
          {(st.phase === "send" || st.fila) && <button onClick={() => { cancel.current = true; }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200" title="Cancelar envio"><X className="h-4 w-4" /></button>}
        </div>
        <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#1E9E2A] transition-all" style={{ width: `${st.phase === "finish" ? 100 : pct}%` }} /></div>
        {st.fila && <div className="mt-1.5 h-1 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#232D4F] transition-all" style={{ width: `${((st.fila.i - 1) / st.fila.n) * 100}%` }} /></div>}
        <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Não feche esta página até terminar.</p>
      </div>
    );
  }
  const botao = (
    <>
      <input ref={input} type="file" accept={"video/mp4,video/quicktime,video/webm,.mp4,.m4v,.mov,.webm" + (docs ? ",.pdf,.xlsx,.xls,.csv,.docx,.doc,.pptx,.ppt,image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif" : "")} className="hidden" multiple={docs}
        onChange={(e) => e.target.files?.length && sendAll(e.target.files)} data-testid="video-upload-input" />
      <Button type="button" variant="outline" onClick={() => input.current?.click()} data-testid="video-upload-btn">
        {st ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} {docs ? "Escolher do computador (pode selecionar vários)" : "Escolher vídeo do computador"}
      </Button>
    </>
  );
  if (!docs) return botao;
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.length) sendAll(e.dataTransfer.files); }}
      className={`rounded-xl border-2 border-dashed p-5 flex flex-col sm:flex-row items-center gap-3 transition-colors ${over ? "border-[#1E9E2A] bg-green-50" : "border-slate-200 bg-slate-50/50"}`} data-testid="library-dropzone">
      {botao}
      <span className="text-sm text-slate-500">ou arraste os arquivos para cá</span>
    </div>
  );
}
